import React from 'react';
import { createPortal } from 'react-dom';

interface ModalShellProps {
  /** 左上角标题 */
  title: React.ReactNode;
  onClose: () => void;
  /** 关闭按钮左边的额外操作（比如「编辑」） */
  headerExtra?: React.ReactNode;
  /** 固定在底部的区域（按钮、评论框） */
  footer?: React.ReactNode;
  /** 点遮罩是否关闭，默认是；有未保存内容时传 false */
  closeOnOverlay?: boolean;
  /** lg：任务类的大弹窗（固定高度）；md：支付、确认这类流程小弹窗（高度随内容） */
  size?: 'lg' | 'md';
  children: React.ReactNode;
}

/**
 * 任务相关弹窗的统一外壳：新建、详情、编辑都用它，宽高、标题区、底栏完全一致。
 * 内容区自己负责左右内边距（px-8），这样内容里可以放通栏的分隔线。
 */
export const ModalShell: React.FC<ModalShellProps> = ({
  title,
  onClose,
  headerExtra,
  footer,
  closeOnOverlay = true,
  size = 'lg',
  children,
}) =>
  createPortal(
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onMouseDown={(e) => { if (closeOnOverlay && e.target === e.currentTarget) onClose(); }}
    >
      <div
        className={`bg-white rounded-xl shadow-2xl w-full flex flex-col ${
          size === 'lg' ? 'max-w-4xl h-[88vh] max-h-[860px]' : 'max-w-xl max-h-[88vh]'
        }`}
        onMouseDown={(e) => e.stopPropagation()}
        onPointerDown={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
        onDoubleClick={(e) => e.stopPropagation()}
      >
        <div className="px-8 pt-6 pb-4 flex items-start justify-between gap-6 shrink-0">
          <h2 className="text-xl font-semibold text-gray-900 break-words min-w-0">{title}</h2>
          <div className="flex items-center gap-3 shrink-0">
            {headerExtra}
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl leading-none" title="关闭 (Esc)">×</button>
          </div>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto">{children}</div>

        {footer && <div className="px-8 py-4 border-t border-gray-100 shrink-0">{footer}</div>}
      </div>
    </div>,
    document.body,
  );
